import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit';
import { HomeBase, ShortestPath, SetHomeBaseRequest } from '../../types/homeBase';
import { homeBaseService } from '../../services/homeBaseService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Lat/lng of a pending map-click before the operator confirms placement. */
export interface PendingPin {
  lat: number;
  lng: number;
}

/**
 * Placement mode:
 *  - idle       — no placement in progress
 *  - placing    — operator has toggled placement on; waiting for map click
 */
export type PlacementMode = 'idle' | 'placing';

interface HomeBaseState {
  /** Home base keyed by droneId. null = not yet fetched; HomeBase = fetched. */
  homeBase: HomeBase | null;
  /** Shortest path for the currently selected drone. */
  shortestPath: ShortestPath | null;
  placementMode: PlacementMode;
  /** Lat/lng the operator clicked before confirming. */
  pendingPin: PendingPin | null;
  /** Cruise speed used when fetching the shortest path (m/s). */
  cruiseSpeedMs: number;
  loading: boolean;
  /** Loading state specifically for setting the home base. */
  settingHomeBase: boolean;
  /** Loading state for the shortest path fetch. */
  loadingPath: boolean;
  error: string | null;
  pathError: string | null;
}

const initialState: HomeBaseState = {
  homeBase: null,
  shortestPath: null,
  placementMode: 'idle',
  pendingPin: null,
  cruiseSpeedMs: 10,
  loading: false,
  settingHomeBase: false,
  loadingPath: false,
  error: null,
  pathError: null,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

export const fetchHomeBase = createAsyncThunk<
  HomeBase,
  string,
  { rejectValue: string }
>('homeBase/fetchHomeBase', async (droneId, { rejectWithValue }) => {
  try {
    return await homeBaseService.getHomeBase(droneId);
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Failed to fetch home base';
    return rejectWithValue(message);
  }
});

export const setHomeBase = createAsyncThunk<
  HomeBase,
  { droneId: string; request: SetHomeBaseRequest },
  { rejectValue: string }
>('homeBase/setHomeBase', async ({ droneId, request }, { rejectWithValue }) => {
  try {
    return await homeBaseService.setHomeBase(droneId, request);
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Failed to set home base';
    return rejectWithValue(message);
  }
});

export const fetchShortestPath = createAsyncThunk<
  ShortestPath,
  { droneId: string; cruiseSpeedMs?: number },
  { rejectValue: string }
>(
  'homeBase/fetchShortestPath',
  async ({ droneId, cruiseSpeedMs }, { rejectWithValue }) => {
    try {
      return await homeBaseService.getShortestPathToHome(droneId, cruiseSpeedMs);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch shortest path';
      return rejectWithValue(message);
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const homeBaseSlice = createSlice({
  name: 'homeBase',
  initialState,
  reducers: {
    /** Operator clicked "Set Home Base" — enter placement mode. */
    startPlacement(state) {
      state.placementMode = 'placing';
      state.pendingPin = null;
      state.error = null;
    },
    /** Operator clicked the map — record the candidate location. */
    setPendingPin(state, action: PayloadAction<PendingPin>) {
      state.pendingPin = action.payload;
    },
    /** Operator pressed Escape or clicked Cancel — abort placement. */
    cancelPlacement(state) {
      state.placementMode = 'idle';
      state.pendingPin = null;
    },
    /** Set cruise speed used for path fetching. */
    setCruiseSpeedMs(state, action: PayloadAction<number>) {
      state.cruiseSpeedMs = action.payload;
    },
    /** Live WS event: a drone's home base was set or changed by anyone. */
    homeBaseUpdated(state, action: PayloadAction<HomeBase>) {
      state.homeBase = action.payload;
      state.error = null;
    },
    /** Live WS event: the shortest path to home was recomputed. */
    pathToHomeUpdated(state, action: PayloadAction<ShortestPath>) {
      state.shortestPath = action.payload;
      state.pathError = null;
    },
    /** Clear all home base state when the selected drone changes. */
    clearHomeBase(state) {
      state.homeBase = null;
      state.shortestPath = null;
      state.placementMode = 'idle';
      state.pendingPin = null;
      state.error = null;
      state.pathError = null;
      state.loading = false;
      state.loadingPath = false;
    },
  },
  extraReducers: (builder) => {
    // fetchHomeBase
    builder
      .addCase(fetchHomeBase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomeBase.fulfilled, (state, action) => {
        state.loading = false;
        state.homeBase = action.payload;
      })
      .addCase(fetchHomeBase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error fetching home base';
      });

    // setHomeBase
    builder
      .addCase(setHomeBase.pending, (state) => {
        state.settingHomeBase = true;
        state.error = null;
      })
      .addCase(setHomeBase.fulfilled, (state, action) => {
        state.settingHomeBase = false;
        state.homeBase = action.payload;
        state.placementMode = 'idle';
        state.pendingPin = null;
      })
      .addCase(setHomeBase.rejected, (state, action) => {
        state.settingHomeBase = false;
        state.error = action.payload ?? 'Unknown error setting home base';
      });

    // fetchShortestPath
    builder
      .addCase(fetchShortestPath.pending, (state) => {
        state.loadingPath = true;
        state.pathError = null;
      })
      .addCase(fetchShortestPath.fulfilled, (state, action) => {
        state.loadingPath = false;
        state.shortestPath = action.payload;
      })
      .addCase(fetchShortestPath.rejected, (state, action) => {
        state.loadingPath = false;
        state.pathError = action.payload ?? 'Unknown error fetching path';
      });
  },
});

export const {
  startPlacement,
  setPendingPin,
  cancelPlacement,
  setCruiseSpeedMs,
  homeBaseUpdated,
  pathToHomeUpdated,
  clearHomeBase,
} = homeBaseSlice.actions;

export default homeBaseSlice.reducer;
